import {
  UserChallengeParticipationQuery,
  useUserChallengeParticipationQuery,
} from 'generated/graphql';
import { useAsyncQuery } from '@client/new/helpers/gql';
import { Async } from '@client/new/helpers/types';
import { useCommunityContext } from '../lib/communityContext';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';
import { AuthorId } from '../model/post.types';

export type ChallengeRef = { id: string; name: string };

export type ChallengeParticipation = {
  active: ChallengeRef[];
  inactive: ChallengeRef[];
};

const toRef = (ref: { id: string; name: string }): ChallengeRef => ({
  id: ref.id,
  name: ref.name,
});

const toParticipation = (
  data: UserChallengeParticipationQuery['userChallengeParticipation'],
): ChallengeParticipation => ({
  active: data.active.map(toRef),
  inactive: data.inactive.map(toRef),
});

// Data hook (Async<T>): loaded lazily when the profile modal opens. Skipped
// while closed or when the community's creator id is unknown, so opening a feed
// of posts issues no participation queries until a profile is opened.
export const useAuthorChallengeParticipation = (
  authorId: AuthorId,
  open: boolean,
): Async<ChallengeParticipation> => {
  const { influencerId } = useCommunityContext();
  const creatorId = resolveInfluencerId(influencerId);

  const query = useUserChallengeParticipationQuery({
    variables: { userId: authorId, creatorId: creatorId ?? 0 },
    skip: !open || creatorId === null,
    fetchPolicy: 'cache-first',
  });

  return useAsyncQuery(query, 'userChallengeParticipation', toParticipation)
    .data;
};
